import type { Priority, TaskStatus } from "@desk/contracts";
import { PRIORITY_LABEL, STATUS_LABEL } from "@desk/contracts";
import { cn } from "@/lib/utils";

/**
 * Linear-style priority glyph: 3 bars (filled up to level), Urgent = filled square with "!".
 * Urgent is orange and High a muted warm; the rest stay neutral. `data-tone` keeps menus from
 * recoloring it, so a priority looks the same in rows, menus, filters, and detail.
 */
export function PriorityIcon({ priority, className, title = true }: { priority: Priority; className?: string; title?: boolean }) {
  const label = PRIORITY_LABEL[priority];
  if (priority === "urgent") {
    return (
      <svg viewBox="0 0 16 16" data-tone className={cn("size-4 text-urgent", className)} role="img" aria-label={label}>
        {title && <title>{label}</title>}
        <rect x="1.5" y="1.5" width="13" height="13" rx="3" fill="currentColor" />
        <rect x="7.1" y="4" width="1.8" height="5" rx="0.9" fill="var(--background)" />
        <circle cx="8" cy="11.3" r="1.05" fill="var(--background)" />
      </svg>
    );
  }
  if (priority === "none") {
    return (
      <svg viewBox="0 0 16 16" data-tone className={cn("size-4 text-subtle", className)} role="img" aria-label={label}>
        {title && <title>{label}</title>}
        <rect x="2" y="7.25" width="2.6" height="1.5" rx="0.75" fill="currentColor" opacity="0.7" />
        <rect x="6.7" y="7.25" width="2.6" height="1.5" rx="0.75" fill="currentColor" opacity="0.7" />
        <rect x="11.4" y="7.25" width="2.6" height="1.5" rx="0.75" fill="currentColor" opacity="0.7" />
      </svg>
    );
  }
  const level = priority === "high" ? 3 : priority === "medium" ? 2 : 1;
  return (
    <svg viewBox="0 0 16 16" data-tone className={cn("size-4", priority === "high" ? "text-priority-high" : "text-foreground/75", className)} role="img" aria-label={label}>
      {title && <title>{label}</title>}
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={2 + i * 4.6}
          y={10 - i * 3.5}
          width="2.8"
          height={4 + i * 3.5}
          rx="0.9"
          fill="currentColor"
          opacity={i < level ? 1 : 0.22}
        />
      ))}
    </svg>
  );
}

/** Linear-style status glyph: backlog and cancelled quiet, To do neutral, In progress blue, Done green. */
export function StatusIcon({ status, className }: { status: TaskStatus; className?: string }) {
  const label = STATUS_LABEL[status];
  const common = { viewBox: "0 0 16 16", role: "img" as const, "aria-label": label, "data-tone": true };
  switch (status) {
    case "backlog":
      return (
        <svg {...common} className={cn("size-4 text-subtle", className)}>
          <title>{label}</title>
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.2 2.1" />
        </svg>
      );
    case "todo":
      return (
        <svg {...common} className={cn("size-4 text-muted-foreground", className)}>
          <title>{label}</title>
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case "in_progress":
      return (
        <svg {...common} className={cn("size-4 text-progress", className)}>
          <title>{label}</title>
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 4.5 A3.5 3.5 0 0 1 8 11.5 Z" fill="currentColor" />
        </svg>
      );
    case "done":
      return (
        <svg {...common} className={cn("size-4 text-success", className)}>
          <title>{label}</title>
          <circle cx="8" cy="8" r="6.75" fill="currentColor" />
          <path d="M5.2 8.2 7.1 10l3.7-4" fill="none" stroke="var(--background)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "cancelled":
      return (
        <svg {...common} className={cn("size-4 text-subtle", className)}>
          <title>{label}</title>
          <circle cx="8" cy="8" r="6.75" fill="currentColor" />
          <path d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4" stroke="var(--background)" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
  }
}

/** Small round glyph for spaces (Paper sidebar). Color-free; uses the first letter. */
export function SpaceGlyph({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-[18px] shrink-0 items-center justify-center rounded-full bg-foreground/[0.08] text-[10px] font-semibold uppercase text-foreground/70",
        className,
      )}
    >
      {name.trim().charAt(0)}
    </span>
  );
}

/** Things-style project glyph: a ring that fills like a pie as tasks complete. */
export function ProjectGlyph({
  state,
  progress,
  className,
}: {
  state?: string;
  progress?: { done: number; total: number };
  className?: string;
}) {
  const frac = state === "completed" ? 1 : progress && progress.total > 0 ? progress.done / progress.total : 0;
  // Pie wedge path from 12 o'clock, clockwise.
  const r = 3.6;
  const angle = Math.min(frac, 0.9999) * 2 * Math.PI;
  const x = 8 + r * Math.sin(angle);
  const y = 8 - r * Math.cos(angle);
  const large = angle > Math.PI ? 1 : 0;
  return (
    <svg viewBox="0 0 16 16" aria-hidden className={cn("size-3.5 shrink-0 text-foreground/55", className)}>
      <circle cx="8" cy="8" r="6.1" fill="none" stroke="currentColor" strokeWidth="1.5" />
      {frac >= 0.9999 ? (
        <circle cx="8" cy="8" r={r} fill="currentColor" />
      ) : frac > 0 ? (
        <path d={`M8 8 L8 ${8 - r} A${r} ${r} 0 ${large} 1 ${x.toFixed(3)} ${y.toFixed(3)} Z`} fill="currentColor" />
      ) : null}
    </svg>
  );
}

/** Desk logo mark ("settle block"), from docs/handoff/brand/logo. Brown in light, cream in dark. */
export function DeskMark({ className }: { className?: string }) {
  return (
    <svg viewBox="343 376 568 495" aria-hidden className={className} fill="var(--brand-mark)">
      <path d="M574 581V428C574 397 598 376 628 384L868 441C894 447 911 466 911 493V791C911 820 892 841 863 841H830V720C830 690 813 669 786 659Z" />
      <path d="M382 662L483 622C502 614 517 613 537 621L742 696C766 705 780 722 780 747V821C780 850 759 871 730 871H480C465 871 455 869 441 864L379 841C356 833 343 816 343 791V714C343 689 356 672 382 662Z" />
    </svg>
  );
}
